import { AddFileParams, convertFile, RepositoryResponse } from '@nextforce/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useServer } from 'app/server/context'
import { addMessagesDataToCache } from 'chat/messages/queries/shared/utils'
import { appendFileToFormData } from 'chat/shared/util'
import { useChatWebsocketContext } from 'chat/websocket/context'
import keys from './shared/keys'
import { addRepositoryFileToCache } from './shared/utils'

export function useAddFile() {
  const { server } = useServer()
  const client = useQueryClient()
  const { sendWebsocketMessage } = useChatWebsocketContext()
  return useMutation({
    mutationFn: addFile,
    onSuccess: async data => {
      const { file, message } = data
      if (file.chatGroupId) {
        addRepositoryFileToCache(client, file, file.chatGroupId)
        const recentFilesKey = keys.recent(file.chatGroupId)
        client.invalidateQueries({ queryKey: recentFilesKey }).catch(() => null)
      }
      if (message) {
        await addMessagesDataToCache(client, [message], undefined)
        sendWebsocketMessage({
          action: 'sendMessageEvent',
          messageEvent: message,
        })
      }
    },
  })

  async function addFile({
    name,
    description,
    parentFolderId,
    file,
    externalUrl,
    chatGroupId,
  }: AddFileParams) {
    const formData = await getFormData()
    const response = await server.post<RepositoryResponse.AddFile>(
      `/v2/chat/repository/files`,
      formData,
      'user',
      'multipart/form-data'
    )
    const fileResponse = convertFile(response.file)
    return { file: fileResponse, message: response.message }

    async function getFormData() {
      const form = new FormData()
      if (name) {
        form.append('name', name)
      }
      if (description) {
        form.append('description', description)
      }
      if (parentFolderId) {
        form.append('folder_id', parentFolderId.toString())
      }

      if (chatGroupId) {
        form.append('chat_group_id', chatGroupId.toString())
      }
      if (externalUrl) {
        form.append('external_url', externalUrl)
      } else {
        if (file) {
          const formFieldName = 'file'
          await appendFileToFormData(form, formFieldName, file)
        }
      }
      return form
    }
  }
}
